import { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/lib/utils';
import { centsToInputDollars, inputDollarsToCents } from '@/lib/formatters';
import {
  PURCHASE_UOM_SUGGESTIONS,
  SIZE_UOM_SUGGESTIONS,
  describePurchaseUnit,
  formatQtyUom,
  formatUnitCostCents,
  pluralUom,
  sameUom,
  unitFactor,
} from '@/lib/uom';
import {
  useAddItemSupplier,
  useSaveItemSupplierPriceBreaks,
  useUpdateItemSupplier,
  type ItemSupplier,
} from '@/features/inventory/hooks';
import { useVendors } from '@/features/procurement/hooks';

interface PriceBreakDraft {
  key: number;
  minQty: string;
  unitCost: string;
}

let priceBreakKey = 0;
const newBreak = (minQty = '', unitCost = ''): PriceBreakDraft => ({
  key: ++priceBreakKey,
  minQty,
  unitCost,
});

interface AddSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The item the vendor is being approved to supply. */
  itemId: string;
  /** Whether this item already has a primary supplier (drives the default of the primary toggle). */
  hasPrimary: boolean;
  /** When set, the dialog edits this existing supplier row instead of adding a new one. */
  supplier?: ItemSupplier | null;
  /** The item's stock unit (items.base_uom) that inventory and BOMs count in. */
  baseUom: string;
}

const PURCHASE_UOM_LIST_ID = 'supplier-purchase-uom-options';
const SIZE_UOM_LIST_ID = 'supplier-size-uom-options';

function parsePositive(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value.replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Stock units in one purchase unit from what the buyer typed: "150 ft"
 * converts on its own when the stock unit is inches; a size in a unit
 * that doesn't convert ("1 box contains 12 bags", stocked in each) needs
 * the stock count entered directly.
 */
function resolveConversion(
  size: string,
  sizeUom: string,
  stockUnits: string,
  baseUom: string,
): { conversion: number | null; needsStockUnits: boolean } {
  const qty = parsePositive(size);
  const unit = sizeUom.trim() || baseUom;
  const factor = unitFactor(unit, baseUom);
  if (factor != null) return { conversion: qty != null ? qty * factor : null, needsStockUnits: false };
  return { conversion: parsePositive(stockUnits), needsStockUnits: true };
}

/**
 * "Add supplier" / "Edit supplier" modal launched from the Suppliers tab.
 *
 * Lets an operator approve a vendor to supply the current item and
 * capture the per-vendor part number, unit cost, lead time, MOQ, and
 * primary flag. The vendor list comes from the org's directory
 * (`useVendors`); the `useAddItemSupplier` / `useUpdateItemSupplier`
 * hooks own the write + the "only one primary per item" demotion.
 */
export function AddSupplierDialog({
  open,
  onOpenChange,
  itemId,
  hasPrimary,
  supplier,
  baseUom,
}: AddSupplierDialogProps) {
  const { data: vendors, isLoading: vendorsLoading } = useVendors();
  const addSupplier = useAddItemSupplier();
  const updateSupplier = useUpdateItemSupplier();
  const savePriceBreaks = useSaveItemSupplierPriceBreaks();
  const isEdit = !!supplier;

  const [query, setQuery] = useState('');
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [partNumber, setPartNumber] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [moq, setMoq] = useState('');
  const [packSize, setPackSize] = useState('');
  const [breaks, setBreaks] = useState<PriceBreakDraft[]>([]);
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState('');
  const [purchaseUom, setPurchaseUom] = useState('');
  const [packQty, setPackQty] = useState('');
  const [packQtyUom, setPackQtyUom] = useState('');
  const [packStockUnits, setPackStockUnits] = useState('');

  // Reset the draft on close. Default the primary flag on when the
  // item has no primary supplier yet — the first vendor you add is
  // almost always the one MRP should prefer.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setVendorId(null);
      setPartNumber('');
      setUnitCost('');
      setLeadTime('');
      setMoq('');
      setPackSize('');
      setBreaks([]);
      setIsPrimary(!hasPrimary);
      setNotes('');
      setPurchaseUom('');
      setPackQty('');
      setPackQtyUom('');
      setPackStockUnits('');
    }
  }, [open, hasPrimary]);

  useEffect(() => {
    if (open && supplier) {
      setQuery('');
      setVendorId(supplier.vendor_id);
      setPartNumber(supplier.supplier_part_number ?? '');
      setUnitCost(centsToInputDollars(supplier.unit_cost_cents));
      setLeadTime(supplier.lead_time_days != null ? String(supplier.lead_time_days) : '');
      setMoq(supplier.min_order_qty != null ? String(Number(supplier.min_order_qty)) : '');
      setPackSize(supplier.order_multiple != null ? String(Number(supplier.order_multiple)) : '');
      setBreaks(
        (supplier.item_supplier_price_breaks ?? []).map((b) =>
          newBreak(String(b.min_qty), centsToInputDollars(b.unit_cost_cents)),
        ),
      );
      setIsPrimary(supplier.is_primary);
      setNotes(supplier.notes ?? '');
      const conv = Number(supplier.units_per_purchase_uom ?? 1) || 1;
      setPurchaseUom(supplier.purchase_uom ?? '');
      if (supplier.purchase_uom) {
        const statedSize = supplier.purchase_uom_size ?? conv;
        const statedUom = supplier.purchase_uom_size_uom ?? baseUom;
        setPackQty(String(Number(statedSize)));
        setPackQtyUom(statedUom);
        setPackStockUnits(unitFactor(statedUom, baseUom) == null ? String(conv) : '');
      } else {
        setPackQty('');
        setPackQtyUom('');
        setPackStockUnits('');
      }
    }
  }, [open, supplier, baseUom]);

  const updateBreak = (key: number, patch: Partial<Omit<PriceBreakDraft, 'key'>>) =>
    setBreaks((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  const removeBreak = (key: number) => setBreaks((prev) => prev.filter((b) => b.key !== key));

  const filtered = useMemo(() => {
    const list = vendors ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.email ?? '').toLowerCase().includes(q),
    );
  }, [vendors, query]);

  const buyUnit = purchaseUom.trim();
  const buysInPacks = !!buyUnit && !sameUom(buyUnit, baseUom);
  const { conversion, needsStockUnits } = resolveConversion(packQty, packQtyUom, packStockUnits, baseUom);
  const packDescription =
    buysInPacks && conversion != null
      ? describePurchaseUnit(
          {
            purchaseUom: buyUnit,
            size: parsePositive(packQty),
            sizeUom: packQtyUom.trim() || baseUom,
            unitsPerPurchaseUom: conversion,
          },
          baseUom,
        )
      : null;
  const priceUnit = buysInPacks ? buyUnit : baseUom;
  const unitCostPreviewCents = (() => {
    if (!buysInPacks || conversion == null) return null;
    try {
      const cents = inputDollarsToCents(unitCost);
      return cents == null ? null : cents / conversion;
    } catch {
      return null;
    }
  })();

  const selected = useMemo(
    () => (vendors ?? []).find((v) => v.id === vendorId) ?? null,
    [vendors, vendorId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      toast.error('Pick a vendor first.');
      return;
    }

    let unitCostCents: number | null;
    try {
      unitCostCents = inputDollarsToCents(unitCost);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid unit cost.');
      return;
    }

    const leadTimeDays = leadTime.trim() ? Number(leadTime) : null;
    if (leadTimeDays != null && (!Number.isFinite(leadTimeDays) || leadTimeDays < 0)) {
      toast.error('Lead time must be a positive number of days.');
      return;
    }
    const minOrderQty = moq.trim() ? Number(moq) : null;
    if (minOrderQty != null && (!Number.isFinite(minOrderQty) || minOrderQty < 0)) {
      toast.error('Minimum order quantity must be a positive number.');
      return;
    }
    const orderMultiple = packSize.trim() ? Number(packSize) : null;
    if (orderMultiple != null && (!Number.isInteger(orderMultiple) || orderMultiple <= 0)) {
      toast.error('Pack size must be a whole number above zero.');
      return;
    }

    if (buysInPacks && conversion == null) {
      toast.error(
        needsStockUnits
          ? `Enter how many ${pluralUom(baseUom, 2)} are in one ${buyUnit}.`
          : `Enter how much is in one ${buyUnit}.`,
      );
      return;
    }

    const parsedBreaks: Array<{ minQty: number; unitCostCents: number }> = [];
    for (const brk of breaks) {
      if (!brk.minQty.trim() && !brk.unitCost.trim()) continue;
      const minQty = Number(brk.minQty);
      if (!Number.isInteger(minQty) || minQty <= 0) {
        toast.error('Each price break needs a whole-number quantity above zero.');
        return;
      }
      let cents: number | null;
      try {
        cents = inputDollarsToCents(brk.unitCost);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Invalid price break price.');
        return;
      }
      if (cents == null) {
        toast.error(`Add a unit price for the ${minQty}+ price break.`);
        return;
      }
      if (parsedBreaks.some((b) => b.minQty === minQty)) {
        toast.error(`Two price breaks start at ${minQty}.`);
        return;
      }
      parsedBreaks.push({ minQty, unitCostCents: cents });
    }

    const payload = {
      itemId,
      vendorId: selected.id,
      supplierPartNumber: partNumber,
      unitCostCents,
      leadTimeDays,
      minOrderQty,
      orderMultiple,
      purchaseUom: buysInPacks ? buyUnit : null,
      purchaseUomSize: buysInPacks ? parsePositive(packQty) : null,
      purchaseUomSizeUom: buysInPacks ? packQtyUom.trim() || baseUom : null,
      unitsPerPurchaseUom: buysInPacks ? conversion : 1,
      isPrimary,
      notes,
    };

    try {
      let saved: ItemSupplier;
      if (supplier) {
        saved = await updateSupplier.mutateAsync({ ...payload, id: supplier.id, currency: supplier.currency });
      } else {
        saved = await addSupplier.mutateAsync(payload);
      }
      const hadBreaks = (supplier?.item_supplier_price_breaks?.length ?? 0) > 0;
      if (parsedBreaks.length > 0 || hadBreaks) {
        await savePriceBreaks.mutateAsync({ itemId, itemSupplierId: saved.id, breaks: parsedBreaks });
      }
      toast.success(supplier ? `Updated ${selected.name}` : `Added ${selected.name} as a supplier`);
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : isEdit
            ? 'Could not update supplier.'
            : 'Could not add supplier.',
      );
    }
  };

  const submitting = addSupplier.isPending || updateSupplier.isPending || savePriceBreaks.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit supplier' : 'Add supplier'}</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label>Vendor</Label>
              <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                <Search className="h-3.5 w-3.5 text-muted-foreground" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search vendors…"
                  className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                {vendorsLoading ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    Loading…
                  </div>
                ) : (vendors ?? []).length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No vendors yet.
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                    No matching vendors.
                  </div>
                ) : (
                  filtered.map((v) => {
                    const isSelected = v.id === vendorId;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setVendorId(v.id)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60',
                          isSelected && 'bg-secondary',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'opacity-40',
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                        <span className="truncate">{v.name}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-baseline justify-between gap-2">
                <Label htmlFor="supplier-purchase-uom">How this vendor sells it</Label>
                <span className="text-xs text-muted-foreground">
                  Stocked in <span className="font-medium text-foreground">{baseUom}</span>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Sold by the</span>
                <Input
                  id="supplier-purchase-uom"
                  value={purchaseUom}
                  onChange={(e) => setPurchaseUom(e.target.value)}
                  placeholder={baseUom}
                  list={PURCHASE_UOM_LIST_ID}
                  className="h-8 w-28"
                />
                <datalist id={PURCHASE_UOM_LIST_ID}>
                  {PURCHASE_UOM_SUGGESTIONS.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
                {buysInPacks && (
                  <>
                    <span className="text-muted-foreground">· 1 {buyUnit} contains</span>
                    <Input
                      value={packQty}
                      onChange={(e) => setPackQty(e.target.value)}
                      placeholder="150"
                      inputMode="decimal"
                      aria-label={`Amount in one ${buyUnit}`}
                      className="h-8 w-20 tabular-nums"
                    />
                    <Input
                      value={packQtyUom}
                      onChange={(e) => setPackQtyUom(e.target.value)}
                      placeholder={baseUom}
                      list={SIZE_UOM_LIST_ID}
                      aria-label={`Unit of the amount in one ${buyUnit}`}
                      className="h-8 w-20"
                    />
                    <datalist id={SIZE_UOM_LIST_ID}>
                      {SIZE_UOM_SUGGESTIONS.map((u) => (
                        <option key={u} value={u} />
                      ))}
                    </datalist>
                  </>
                )}
              </div>
              {buysInPacks && needsStockUnits && (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {packQtyUom.trim() || 'That'} doesn&apos;t convert to {baseUom} on its own — 1 {buyUnit} is
                  </span>
                  <Input
                    value={packStockUnits}
                    onChange={(e) => setPackStockUnits(e.target.value)}
                    placeholder="0"
                    inputMode="decimal"
                    aria-label={`${baseUom} in one ${buyUnit}`}
                    className="h-8 w-24 tabular-nums"
                  />
                  <span className="text-muted-foreground">{baseUom}</span>
                </div>
              )}
              {buysInPacks ? (
                packDescription ? (
                  <p className="rounded bg-secondary/60 px-2 py-1.5 text-sm font-medium tabular-nums">
                    {packDescription}
                    <span className="block text-xs font-normal text-muted-foreground">
                      POs, unit cost, MOQ and pack size below are in {pluralUom(buyUnit, 2)}. Receiving{' '}
                      {formatQtyUom(4, buyUnit)} adds {formatQtyUom(4 * (conversion ?? 1), baseUom)} to stock.
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Enter how much is in one {buyUnit} so receipts add the right amount of {baseUom} to stock.
                  </p>
                )
              ) : (
                <p className="text-xs text-muted-foreground">
                  Leave blank when you order in {baseUom}. Set it (roll, box, spool…) when the vendor quotes
                  per pack — e.g. webbing sold by the roll but stocked in inches.
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="supplier-part">Part number</Label>
                <Input
                  id="supplier-part"
                  value={partNumber}
                  onChange={(e) => setPartNumber(e.target.value)}
                  placeholder="Vendor's SKU"
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-cost">Unit cost (per {priceUnit})</Label>
                <Input
                  id="supplier-cost"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  className="tabular-nums"
                />
                {unitCostPreviewCents != null && (
                  <p className="text-xs text-muted-foreground tabular-nums">
                    = {formatUnitCostCents(unitCostPreviewCents)} per {baseUom}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-lead">Lead time (days)</Label>
                <Input
                  id="supplier-lead"
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                  placeholder="0"
                  inputMode="numeric"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-moq">Min order qty ({pluralUom(priceUnit, 2)})</Label>
                <Input
                  id="supplier-moq"
                  value={moq}
                  onChange={(e) => setMoq(e.target.value)}
                  placeholder="0"
                  inputMode="numeric"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="supplier-pack">Order multiple ({pluralUom(priceUnit, 2)})</Label>
                <Input
                  id="supplier-pack"
                  value={packSize}
                  onChange={(e) => setPackSize(e.target.value)}
                  placeholder="Sold in multiples of…"
                  inputMode="numeric"
                  className="tabular-nums"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Price breaks</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setBreaks((prev) => [...prev, newBreak()])}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add break
                </Button>
              </div>
              {breaks.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Optional. Price per {priceUnit} when ordering at least a number of{' '}
                  {pluralUom(priceUnit, 2)} — the planner shows when buying up to the next break costs
                  less overall.
                </p>
              ) : (
                <div className="space-y-2">
                  {breaks.map((brk) => (
                    <div key={brk.key} className="flex items-center gap-2">
                      <Input
                        value={brk.minQty}
                        onChange={(e) => updateBreak(brk.key, { minQty: e.target.value })}
                        placeholder="Qty"
                        inputMode="numeric"
                        aria-label="Price break minimum quantity"
                        className="tabular-nums"
                      />
                      <span className="shrink-0 text-xs text-muted-foreground">+ at</span>
                      <Input
                        value={brk.unitCost}
                        onChange={(e) => updateBreak(brk.key, { unitCost: e.target.value })}
                        placeholder="0.00"
                        inputMode="decimal"
                        aria-label="Price break unit price"
                        className="tabular-nums"
                      />
                      <span className="shrink-0 text-xs text-muted-foreground">per {priceUnit}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => removeBreak(brk.key)}
                        aria-label="Remove price break"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={isPrimary}
                onCheckedChange={setIsPrimary}
                ariaLabel="Set as primary supplier"
              />
              <span>Primary supplier (the default MRP / PO picks)</span>
            </label>

            <div className="space-y-1.5">
              <Label htmlFor="supplier-notes">Notes</Label>
              <Textarea
                id="supplier-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this vendor relationship."
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !selected}>
              {isEdit
                ? submitting
                  ? 'Saving…'
                  : 'Save changes'
                : submitting
                  ? 'Adding…'
                  : 'Add supplier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
